import { useCallback, useRef } from "react";
import { useMerchantWebPortalFormData } from "../../utils/axiosWeb";
import useMerchantPortalAxios from "../../utils/axios";
import { normalizeLastId } from "../utils/loadMore";

function normalizeTaxReceiptPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const entityPaymentId = String(payload.Entity_Payment_ID || "").trim();
  const entityIdDigits = String(entityPaymentId).match(/\d+/)?.[0] || "";
  const numericEntityId = Number(entityIdDigits);
  const entityId =
    payload.EntityId ??
    payload.EntityID ??
    (Number.isSafeInteger(numericEntityId)
      ? numericEntityId
      : entityIdDigits);
  const name = String(payload.Name || "").trim();
  const phoneNumber = String(payload.Phone_Number || "").trim();
  const blobUrl = String(
    payload.blobUrl ||
      payload.BlobUrl ||
      payload.blobURL ||
      payload.BlobURL ||
      "",
  ).trim();
  const amountText = String(payload.Amount ?? "")
    .replace(/,/g, "")
    .trim();
  const parsedAmount = Number(amountText);
  const parsedFees = Number(
    String(payload.fees ?? 0)
      .replace(/,/g, "")
      .trim(),
  );
  const deliveryFeeRaw = payload.DeliveryFees ?? payload.Delivery_Fees ?? null;
  const parsedSuyoolDeliveryFee =
    deliveryFeeRaw === null ||
    deliveryFeeRaw === undefined ||
    deliveryFeeRaw === ""
      ? null
      : Number(String(deliveryFeeRaw).replace(/,/g, "").trim());
  const parsedTotalAmount = Number(
    String(
      payload.totalAmount ??
        parsedAmount + (Number.isNaN(parsedFees) ? 0 : parsedFees),
    )
      .replace(/,/g, "")
      .trim(),
  );

  if (
    !entityPaymentId ||
    !name ||
    !phoneNumber ||
    !amountText ||
    !Number.isFinite(parsedAmount) ||
    parsedAmount <= 0
  ) {
    return null;
  }

  return {
    entityId,
    entityPaymentId,
    name,
    phoneNumber,
    amount: parsedAmount,
    fees: Number.isNaN(parsedFees) ? 0 : parsedFees,
    suyoolDeliveryFee: Number.isNaN(parsedSuyoolDeliveryFee)
      ? null
      : parsedSuyoolDeliveryFee,
    totalAmount: Number.isNaN(parsedTotalAmount)
      ? parsedAmount + (Number.isNaN(parsedFees) ? 0 : parsedFees)
      : parsedTotalAmount,
    blobUrl,
  };
}

export default function useTaxPaymentApi() {
  const formClient = useMerchantWebPortalFormData();
  const client = useMerchantPortalAxios();
  const formClientRef = useRef(formClient);
  const clientRef = useRef(client);
  formClientRef.current = formClient;
  clientRef.current = client;

  const getCorporateTvaOverview = useCallback(
    async ({ pageSize = 10, lastId = 0, type } = {}) => {
      const sessionId = sessionStorage.getItem("SessionID") || "";
      const normalizedLastId = normalizeLastId(lastId);

      const response = await clientRef.current.get(
        "/v1/CorporateTVA/GetCorporateTvaOverview",
        {
          params: {
            lastId: normalizedLastId,
            pageSize,
            sessionID: sessionId,
            typeId: type
          },
        },
      );

      return Array.isArray(response?.data?.Payload)
        ? response.data.Payload
        : [];
    },
    [],
  );

  const extractTaxReceipt = useCallback(async (file, taxTypeId) => {
    const formData = new FormData();
    formData.append("file", file, file?.name || "vat-receipt.pdf");
    formData.append("SessionID", sessionStorage.getItem("SessionID") || "");
    formData.append("TaxTypeId", String(taxTypeId));

    const response = await formClientRef.current.post(
      "/api/pdf/extract-vat-receipt",
      formData,
      {
        headers: {
          "X-API-Key": process.env.REACT_APP_UTILITIES_API_KEY || "",
          Authorization: sessionStorage.getItem("Token"),
        },
      },
    );

    const normalized = normalizeTaxReceiptPayload(response?.data);

    if (!normalized) {
      const error = new Error("The uploaded receipt is missing required fields.");
      error.code = "INVALID_DOCUMENT";
      throw error;
    }

    return normalized;
  }, []);

  const saveCorporateTvaData = useCallback(async (payload) => {
    const response = await clientRef.current.post(
      "/v1/CorporateTVA/SaveCorporateTvaData",
      payload,
    );

    return response?.data?.Payload || response?.data;
  }, []);

  return {
    getCorporateTvaOverview,
    extractTaxReceipt,
    saveCorporateTvaData,
  };
}
